import { findStructure, useStore_client_access } from '~/stores/client/access'
import { ERRORS as LAYOUT_ERRORS } from '~/utils/lib/layout/types'
import { includes, singleton, typeOf, reduce, allPass, concat, Maybe, clone } from '~/utils/lib/core/prelude'
import { ACLS } from '@ghsfr/acls-commons'
import { FEATURES, useStore_layout_flags } from '~/stores/layout/flags'
import { navigationPerimeterBoilerplate } from '~/stores/layout/navigation'
/**
 * @desc Facade composable for access control methods
 */
export function useAccessControl() {
  const navigationPerimeter = inject(
    'navigationPerimeter',
    clone(navigationPerimeterBoilerplate)
  )
  const getStructureId = () => navigationPerimeter.structure_id || ''

  const store_client_access = useStore_client_access()

  return {
    getACL: () => {
      const structure_id = getStructureId()

      return getACL(
        store_client_access.data,
        structure_id
      )
    },
    hasACL: (/** @type {ACLS|ACLS[]} */ aclOrAcls) => hasACL(
      store_client_access.data,
      getStructureId(),
      aclOrAcls
    ),
    hasFeature,
    ACLS,
    FEATURES,
  }
}
/// ====================================================================
/**
 * @function getACL
 * @desc
 * @param {import('~/stores/client/access').Access_v3} accessData
 * @param {import('~/stores/client/access').Structure["id"]} structure_id
 * @throws {Error}
 * @returns {Array<import('@ghsfr/acls-commons').ACLS>}
 */
function getACL(accessData, structure_id) {
  const mStructure = findStructure(accessData, structure_id)

  if (Maybe.isNothing(mStructure))
    throw new Error(LAYOUT_ERRORS.UNKNOWN_STRUCTURE)

  const structure = Maybe.fromJust(mStructure)

  return structure.acl
}
/// ====================================================================
/**
 * @function hasACL
 * @desc
 *    Checking provided ACL with current Structure ACL list
 * @template {import('@ghsfr/acls-commons').ACLS[]|import('@ghsfr/acls-commons').ACLS} T
 * @param {import('~/stores/client/access').Access_v3} accessData
 * @param {import('~/stores/client/access').Structure["id"]} structure_id
 * @param {T} aclOrAcls
 * @returns {boolean}
 */
export function hasACL(accessData, structure_id, aclOrAcls) {
  // @ts-ignore: UNSAFE coerce
  const /** @type {import('@ghsfr/acls-commons').ACLS[]} */ acls = typeOf(aclOrAcls) === 'array' ?
    aclOrAcls :
    singleton(aclOrAcls)

  try {
    const availableACL = getACL(accessData, structure_id)

    return allPass_(availableACL, acls)

  } catch (_) { return false }
}
/// ====================================================================
/**
 * @function hasFeature
 * @desc
 *    `AllPass` validator for provided flag features
 * @template {import('~/stores/layout/flags').FEATURES|import('~/stores/layout/flags').FEATURES[]} T
 * @param {T} featureOrFeatures
 * @returns {boolean}
 */
export function hasFeature(featureOrFeatures) {
  // @ts-ignore: UNSAFE coerce
  const /** @type {import('~/stores/layout/flags').FEATURES[]} */ features = typeOf(featureOrFeatures) === 'array' ?
    featureOrFeatures :
    singleton(featureOrFeatures)

  const config = useStore_layout_flags().config

  return allPass_(config, features)
}
/// ====================================================================
/** @returns {boolean} */
function allPass_(
  /** @type {Array<string>} */ stackList,
  /** @type {Array<string>} */ needleList
) {
  const predicates = reduce.iterable(
    // @ts-expect-error: need a transform function
    (acc, needle) => concat(
      acc,
      // @ts-expect-error: need a transform function
      [ () => includes(needle, stackList) ]
    ),
    [],
    needleList
  )

  // @ts-expect-error: need a transform function
  return allPass(predicates)()
}
